// Közös entitás-mutáció hook (create / update / delete) — optimistic update + rollback, egységes hiba-terelés.
//
// Miért kell: a ts-rest react-query mutációja a 4xx-et NEM dobja (a promise a `{ status, body }`-vel
// resolve-ol), így a beépített onError sosem futna és az optimista rollback elmaradna. Ez a hook a
// hívó `mutationFn`-jét (ami a ts-rest kliens-hívást adja vissza) becsomagolja: 4xx/5xx → dobott
// `EntityHttpError` → react-query onError → rollback + státusz-alapú terelés:
//   422 → mező-hibák a form-ra (zod issue path → setFieldError)
//   409 → OCC-ütközés toast + `onConflict` (reload-ajánlat)
//   403 → jog-hiányos toast
//   egyéb → generikus hiba-toast
// Siker → siker-toast; a végén (onSettled) a lista/detail cache invalidálása.
//
// A hook i18n-agnosztikus: a toast-szövegeket a hívó adja (a `toast:` réteg feloldva).
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { toast } from 'sonner';

/** A create Idempotency-Key fejlécéhez — a kettős-POST elleni védelem (az Odin foundation érvényesíti). */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** A ts-rest kliens-hívás nyers eredménye (siker és hiba egyaránt ide resolve-ol). */
export interface EntityMutationResult {
  status: number;
  body: unknown;
}

/** 4xx/5xx → dobott hiba, hogy a react-query error-ága (rollback + terelés) fusson. */
export class EntityHttpError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`HTTP ${String(status)}`);
    this.name = 'EntityHttpError';
  }
}

/** A 422-válasz alakja (contract: `{ message, errors: Record<field, string[]> }`). */
interface ValidationErrorBody {
  message?: string;
  errors?: Record<string, string[]>;
}

function isValidationBody(body: unknown): body is ValidationErrorBody {
  return typeof body === 'object' && body !== null && 'errors' in body;
}

/** Optimista cache-írás egy megadott query-kulcsra (snapshot + updater a rollbackhoz). */
export interface OptimisticConfig<TSnapshot, TVars = unknown> {
  queryKey: QueryKey;
  /**
   * A jelenlegi cache-értékből az új (optimista) értéket számolja; undefined → nincs írás.
   *
   * A MÁSODIK paraméter a mutáció VÁLTOZÓJA. Enélkül a hívónak külső ref-en kellene átadnia a választott
   * értéket, az `onMutate` viszont ASZINKRON (`cancelQueries`-t vár) — két gyors kattintás közt a ref már a
   * MÁSODIK értéket hordozná, miközben az ELSŐ kérés még az elsőt küldi. A változó ezt a rést zárja.
   */
  update: (previous: TSnapshot | undefined, vars: TVars) => TSnapshot | undefined;
}

export interface UseEntityMutationOptions<TVars, TSnapshot> {
  /** A tényleges hívás (ts-rest kliens) — a `{ status, body }`-vel resolve-ol MINDEN státuszra. */
  mutationFn: (vars: TVars) => Promise<EntityMutationResult>;
  /** A művelet után invalidálandó cache-kulcs(ok) (lista + detail). */
  invalidateKeys: QueryKey[];
  /** Opcionális optimista cache-írás (pl. detail-mező vagy lista-sor eltávolítás). */
  optimistic?: OptimisticConfig<TSnapshot, TVars>;
  /** 422 → mező-hiba a form-ra (react-hook-form setError adaptere). */
  onFieldErrors?: (field: string, messages: string[]) => void;
  /** 409 → az OCC-ütközés utáni reload-ajánlat (pl. detail újratöltés). */
  onConflict?: () => void;
  /** A státusz-alapú toast-szövegek (a `toast:` rétegből feloldva). */
  messages: {
    success: string;
    conflict: string;
    forbidden: string;
    validation: string;
    error: string;
    /**
     * ÁTMENETI upstream-zavar (502 · 503 · 429) — a felhasználónak MÁS a teendője, mint egy végleges
     * hibánál: várni és újrapróbálni. Ez a hibaosztály a repóban kétszer harapott már (a fojtás előbb
     * kidobásnak, majd jog-hibának látszott), és a generikus „a művelet most nem sikerült" pont azt a
     * fogódzót vette el, ami segített volna. Elhagyva a `error` szöveg marad.
     */
    unavailable?: string;
  };
}

interface MutationContext<TSnapshot> {
  previous?: TSnapshot | undefined;
}

/**
 * Egy entitás-művelet (create/update/delete) optimista mutációja. A visszaadott objektum a react-query
 * `useMutation` eredménye (`mutate`/`mutateAsync`/`isPending`/…). A hívó a siker/hiba-terelést a
 * konfiggal vezérli — a hook-on kívül nem kell manuális onError/onSuccess.
 */
export function useEntityMutation<TVars, TSnapshot = unknown>(
  options: UseEntityMutationOptions<TVars, TSnapshot>,
) {
  const queryClient = useQueryClient();

  return useMutation<EntityMutationResult, unknown, TVars, MutationContext<TSnapshot>>({
    mutationFn: async (vars) => {
      const res = await options.mutationFn(vars);
      if (res.status >= 400) throw new EntityHttpError(res.status, res.body);
      return res;
    },
    onMutate: async (vars): Promise<MutationContext<TSnapshot>> => {
      const opt = options.optimistic;
      if (!opt) return {};
      await queryClient.cancelQueries({ queryKey: opt.queryKey });
      const previous = queryClient.getQueryData<TSnapshot>(opt.queryKey);
      queryClient.setQueryData<TSnapshot>(opt.queryKey, (old) => opt.update(old, vars));
      return { previous };
    },
    onError: (err, _vars, context) => {
      // Rollback az optimista íráshoz.
      const opt = options.optimistic;
      if (opt && context && 'previous' in context) {
        queryClient.setQueryData<TSnapshot>(opt.queryKey, context.previous);
      }
      // Státusz-alapú terelés.
      if (err instanceof EntityHttpError) {
        if (err.status === 422) {
          const fieldErrors = isValidationBody(err.body) ? (err.body.errors ?? {}) : {};
          if (options.onFieldErrors) {
            for (const [field, messages] of Object.entries(fieldErrors)) {
              options.onFieldErrors(field, messages);
            }
          }
          // MEZŐHIBA NÉLKÜLI 422 (üzleti szabály, pl. „elsődleges számlázási profil szükséges"): a szerver
          // whitelistelt, magyar üzenete mondja meg, mi a baj. A „kiemelt mezők" szöveg ilyenkor semmit sem
          // jelölt — mért eset: egy fiókot nem lehetett felvenni, és a felület nem árulta el, miért.
          const serverMessage =
            typeof err.body === 'object' && err.body !== null && 'message' in err.body
              ? (err.body as { message?: unknown }).message
              : undefined;
          toast.error(
            Object.keys(fieldErrors).length === 0 &&
              typeof serverMessage === 'string' &&
              serverMessage !== ''
              ? serverMessage
              : options.messages.validation,
          );
          return;
        }
        if (err.status === 409) {
          toast.error(options.messages.conflict);
          options.onConflict?.();
          return;
        }
        if (err.status === 403) {
          toast.error(options.messages.forbidden);
          return;
        }
        // 428 = hiányzó `If-Match`: a sor verziója elcsúszott alattunk — ugyanaz a teendő, mint 409-nél.
        if (err.status === 428) {
          toast.error(options.messages.conflict);
          options.onConflict?.();
          return;
        }
        if (err.status === 502 || err.status === 503 || err.status === 429) {
          toast.error(options.messages.unavailable ?? options.messages.error);
          return;
        }
      }
      toast.error(options.messages.error);
    },
    onSuccess: () => {
      toast.success(options.messages.success);
    },
    onSettled: () => {
      for (const key of options.invalidateKeys) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });
}
